import { useEffect } from "react";
import { format, parse } from "date-fns";
import { signal, useSignal } from "@preact/signals-react";

import DatePicker from "componentsv2/form/DatePicker";
import Dropdown from "componentsv2/form/Dropdown";
import {
  MessageContainer,
  useMessage,
} from "componentsv2/experimental/Message";
import Button from "componentsv2/button/Button";
import BasicTable from "componentsv2/table/BasicTable";
import SearchBar from "componentsv2/widgets/SearchBar";
import { layout } from "./ModelEmployee";
import { useWizard } from "storev2/wizardStore";
import { useTable } from "storev2/tableStore";
import { getSections, getPositions } from "./services";
import proxy from "api/proxy";

const layoutEmployee = layout();

function WizardCreateShifts(props) {
  const { signalReset } = props;
  const searchText = useSignal("");
  const { messages, success, error: setError, hide, warning } = useMessage();
  const { store } = useWizard();
  const { getTable, presetTable, reload, resetSelected } = useTable();

  const storeId = "staff.shift_schedule.wizard_employees";
  const storeTable = useTable((state) => state[storeId]);

  const {
    selectedStart,
    selectedEnd,
    selectedSection,
    selectedKind,
    selectedPosition,
  } = props.args.initialState;

  const fieldStart = {
    label: "staff.shift_scheduling.start_date",
    name: "start_date",
    onChange: onChange,
    required: true,
  };

  const fieldEnd = {
    label: "staff.shift_scheduling.end_date",
    name: "end_date",
    onChange: onChange,
    required: true,
  };

  function handleSearch(value) {
    searchText.value = value || "";
    resetSelected(storeId);

    let domain = [];
    if (searchText.value.trim() !== "") {
      const searchVal = `%${searchText.value.trim()}%`;
      domain = [
        "OR",
        ["party.name", "ilike", searchVal],
        ["party.id_number", "ilike", searchVal],
        ["code", "ilike", searchVal],
      ];
    }
    reload(storeId, { search: domain, offset: 0 });
  }

  function onChange(name, value) {
    store[name] = value;
  }

  async function onClickAdd() {
    if (!store.start_date || !store.end_date) {
      warning("Las fechas de inicio y fin son obligatorias.");
      return;
    }

    const start = parse(store.start_date, "yyyy-MM-dd", new Date());
    const end = parse(store.end_date, "yyyy-MM-dd", new Date());

    if (end < start) {
      warning("La fecha final debe ser mayor o igual a la inicial.");
      return;
    }

    if (!store.section) {
      warning("Debe seleccionar una sección.");
      return;
    }

    if (!store.kind) {
      warning("Debe seleccionar un turno.");
      return;
    }

    const table = getTable(storeId);
    const selectedMap = table?.selected;
    const employeesList = selectedMap ? Array.from(selectedMap.values()) : [];

    if (employeesList.length === 0) {
      warning("Debe seleccionar al menos un empleado.");
      return;
    }

    const model = "staff.shift";
    const fields_names = ["id"];
    const range = [];

    let currentDate = new Date(start);
    while (currentDate <= end) {
      range.push(format(currentDate, "yyyy-MM-dd"));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const employeeIds = employeesList.map((e) => e.id);
    const { data: contracts } = await proxy.search(
      "staff.contract",
      [
        ["employee", "in", employeeIds],
        ["state", "=", "active"],
      ],
      ["employee", "finished_date"],
    );
    const contractMap = new Map();
    for (const c of contracts || []) {
      const empId = typeof c.employee === "object" ? c.employee.id : c.employee;
      contractMap.set(Number(empId), c);
    }

    for (const emp of employeesList) {
      if (emp.contracting_state === "outsourcing") continue;
      const contract = contractMap.get(Number(emp.id));
      const empName = emp["party."].rec_name
      if (!contract) {
        warning(`El empleado ${empName} no cuenta con contrato activo.`);
        return;
      }
      if (contract.finished_date) {
        const finishedStr = contract.finished_date;
        const invalidDate = range.find((dt) => dt > finishedStr);
        if (invalidDate) {
          warning(
            `El empleado ${empName} tiene fecha de desvinculación el ${finishedStr}. No se puede programar en fecha posterior (${invalidDate}).`,
          );
          return;
        }
      }
    }

    const recordsToCreate = [];
    for (const employee of employeesList) {
      for (const dt of range) {
        recordsToCreate.push({
          id: null,
          shift_date: dt,
          employee: employee.id,
          shift_kind: store.kind?.id,
          section: store.section?.id,
          position: store.position?.id,
        });
      }
    }

    if (recordsToCreate.length === 0) {
      warning("No se generaron turnos válidos para guardar.");
      return;
    }

    const res = await proxy.create(model, recordsToCreate, fields_names);
    if (res && res.error) {
      setError(res.error);
      return;
    }

    success("¡Turnos agregados exitosamente!");

    resetSelected(storeId);

    if (props.args && props.args.refetch) {
      await props.args.refetch();
    }

    setTimeout(() => {
      hide();
    }, 3000);
  }

  const attrsEmployees = {
    name: "employees",
    layout: layoutEmployee,
    selectable: "multi",
    currentRecordId: signal(null),
  };

  const attrsSection = {
    label: "staff.shift_scheduling.section",
    name: "section",
    type: "many2one",
    model: "staff.section",
    onChange: onChange,
    searchable: true,
    preloaded: true,
    preOptions: getSections,
    required: true,
  };

  const attrsKind = {
    label: "staff.shift.shift_kind",
    name: "kind",
    type: "many2one",
    model: "staff.shift.kind",
    onChange: onChange,
    preOptions: () => Array.from(props.args.shifts.values()),
    preloaded: true,
    required: true,
  };

  const attrsPosition = {
    label: "staff.shift.position",
    name: "position",
    type: "many2one",
    model: "staff.position",
    onChange: onChange,
    preOptions: getPositions,
    preloaded: true,
  };

  function resetWizard() {
    const todayStr = format(new Date(), "yyyy-MM-dd");
    selectedStart.value = todayStr;
    selectedEnd.value = todayStr;
    store.start_date = todayStr;
    store.end_date = todayStr;

    if (selectedSection?.value) {
      store.section = selectedSection.value;
    } else {
      store.section = null;
    }

    if (selectedKind?.value) {
      store.kind = selectedKind.value;
    } else {
      store.kind = null;
    }

    if (selectedPosition?.value) {
      store.position = selectedPosition.value;
    } else {
      store.position = null;
    }

    searchText.value = "";
    resetSelected(storeId);

    presetTable(storeId, layoutEmployee, null).then(() => {
      reload(storeId, { search: [], offset: 0 });
    });
  }

  useEffect(() => {
    resetWizard();
  }, [signalReset]);

  return (
    <div className="h-full flex flex-col gap-y-3">
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col gap-y-3 shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <Dropdown {...attrsSection} data={selectedSection} />
          <Dropdown {...attrsKind} data={selectedKind} />
          <Dropdown {...attrsPosition} data={selectedPosition} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <DatePicker {...fieldStart} data={selectedStart} />
          <DatePicker {...fieldEnd} data={selectedEnd} />
          <div className="flex justify-end">
            <Button
              onClick={onClickAdd}
              name="add"
              color="blue"
              content="staff.shift_scheduling.add"
              style="w-full md:w-auto px-6 h-10"
            />
          </div>
        </div>
      </div>

      <div className="w-full shrink-0">
        <SearchBar handleSearch={handleSearch} />
      </div>
      <MessageContainer messages={messages} onMessageClose={hide} />
      <div className="flex-1 min-h-0 mt-1">
        <BasicTable
          {...attrsEmployees}
          storeId={storeId}
          records={storeTable?.records || []}
        />
      </div>
    </div>
  );
}

export default WizardCreateShifts;
